/**
 * Visitor acquisition tracking (first touch + last touch).
 *
 * Runs on every page load of the SPA/prerendered site, so a visitor who lands
 * on ANY of the 100+ pages is tracked. The first landing (page + referrer host
 * + UTM labels) is kept in localStorage for 90 days, so a visitor who finds us
 * on /compare/kora-vs-freshdesk via Google and signs up ten days later directly
 * is still attributed to that page and channel. The latest non-direct arrival is
 * kept as "last touch". Both are sent to the API with sign-in and workspace
 * creation (see real-api.ts).
 *
 * Privacy: stores only the path (no query string), the referrer HOST (no path
 * or query), three sanitised UTM labels, timestamps and a visit counter. No
 * identifiers, no click ids. Every storage access is guarded: blocked or full
 * storage simply disables tracking.
 */

const KEY = 'kora_attr_v1';
const SESSION_KEY = 'kora_attr_session';
const TTL_MS = 90 * 24 * 3600 * 1000;
const MAX_TOUCHES = 5;

export interface Touch {
  p: string | null; // landing path
  h: string | null; // referrer host (external only)
  us: string | null; // utm_source
  um: string | null; // utm_medium
  uc: string | null; // utm_campaign
  t: number; // epoch ms
}

export interface Attribution {
  v: 1;
  first: Touch;
  last: Touch;
  visits: number;
  touches: Touch[];
}

function cleanUtm(v: string | null): string | null {
  if (!v) return null;
  const s = v.trim().toLowerCase().slice(0, 64);
  return /^[a-z0-9._+\- ]+$/.test(s) ? s : null;
}

function read(): Attribution | null {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    const a = JSON.parse(raw) as Attribution;
    if (a?.v !== 1 || !a.first || typeof a.first.t !== 'number') return null;
    if (Date.now() - a.first.t > TTL_MS) return null;
    return a;
  } catch {
    return null;
  }
}

function write(a: Attribution) {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(a));
  } catch {
    /* storage blocked/full: tracking disabled */
  }
}

function isNewVisit(): boolean {
  try {
    if (window.sessionStorage.getItem(SESSION_KEY)) return false;
    window.sessionStorage.setItem(SESSION_KEY, '1');
    return true;
  } catch {
    return true;
  }
}

function currentTouch(): Touch {
  const params = new URLSearchParams(window.location.search);
  let host: string | null = null;
  try {
    if (document.referrer) {
      const h = new URL(document.referrer).hostname.toLowerCase();
      const own = window.location.hostname.toLowerCase();
      if (h && h !== own && !h.endsWith('.' + own) && /^[a-z0-9.-]{1,100}$/.test(h)) host = h;
    }
  } catch {
    /* ignore malformed referrer */
  }
  return {
    p: window.location.pathname.slice(0, 200),
    h: host,
    us: cleanUtm(params.get('utm_source')),
    um: cleanUtm(params.get('utm_medium')),
    uc: cleanUtm(params.get('utm_campaign')),
    t: Date.now(),
  };
}

const isDirect = (t: Touch) => !t.h && !t.us && !t.um;

/** Call once on app start (client only). Safe to call repeatedly. */
export function captureAttribution(): void {
  try {
    if (typeof window === 'undefined') return;
    // Skip embedded/iframe views and embedded customer sessions: those are our
    // customers' visitors, not people discovering Kora.
    if (window.top !== window.self) return;
    if (window.location.pathname.startsWith('/embedd')) return;
    if (new URLSearchParams(window.location.search).has('embed')) return;

    const newVisit = isNewVisit();
    if (!newVisit) return;

    const touch = currentTouch();
    const existing = read();

    if (!existing) {
      write({ v: 1, first: touch, last: touch, visits: 1, touches: [touch] });
      return;
    }

    existing.visits = Math.min(existing.visits + 1, 9999);
    // A returning direct visit keeps the earlier last touch; an external arrival
    // (search, AI assistant, link, campaign) becomes the new last touch.
    if (!isDirect(touch)) {
      existing.last = touch;
      const all = [...existing.touches, touch];
      // Keep the very first touch plus the most recent ones.
      existing.touches = all.length > MAX_TOUCHES ? [all[0], ...all.slice(-(MAX_TOUCHES - 1))] : all;
    }
    write(existing);
  } catch {
    /* never break the page */
  }
}

/** Attribution to send with sign-in / workspace creation, or undefined. */
export function getAttribution(): Attribution | undefined {
  try {
    if (typeof window === 'undefined') return undefined;
    return read() ?? undefined;
  } catch {
    return undefined;
  }
}
